import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";

const DIAS_SEMANA = [
  { value: 1, label: "Seg" },
  { value: 2, label: "Ter" },
  { value: 3, label: "Qua" },
  { value: 4, label: "Qui" },
  { value: 5, label: "Sex" },
  { value: 6, label: "Sáb" },
  { value: 0, label: "Dom" },
];

const TIPOS_LEMBRETE = [
  { value: "mecom_bancada", label: "Resumo Bancada MECOM" },
  { value: "resolve_solicitacoes", label: "Resumo Resolve" },
];

function diasSemanaToArray(str: string): number[] {
  if (!str) return [];
  return str.split(",").map(Number).filter((n) => !isNaN(n));
}

function arrayToDiasSemana(arr: number[]): string {
  return arr.join(",");
}

function formatDiasSemana(str: string): string {
  const arr = diasSemanaToArray(str);
  return DIAS_SEMANA.filter((d) => arr.includes(d.value))
    .map((d) => d.label)
    .join(", ");
}

function formatTipo(tipo: string): string {
  return TIPOS_LEMBRETE.find((t) => t.value === tipo)?.label ?? tipo;
}

const emptyForm = {
  emitenteId: null as number | null,
  whatsapp: "",
  tipo: "mecom_bancada" as "mecom_bancada" | "resolve_solicitacoes",
  diasSemana: [] as number[],
  horario: "",
  ativo: true,
};

export default function LembretesGestao() {
  const [emitenteIdFiltro, setEmitenteIdFiltro] = useState<number | undefined>(undefined);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState({ ...emptyForm });

  const emitentesQuery = trpc.emitentes.list.useQuery(undefined, { staleTime: 60000 });
  const emitentes = (emitentesQuery.data ?? []) as any[];

  const agendamentosQuery = trpc.lembretes.agendamentos.list.useQuery(
    { emitenteId: emitenteIdFiltro },
    { enabled: true }
  );
  const agendamentos = (agendamentosQuery.data ?? []) as any[];

  const disparosQuery = trpc.lembretes.disparos.list.useQuery(
    { emitenteId: emitenteIdFiltro, limit: 200 },
    { enabled: true }
  );
  const disparos = (disparosQuery.data ?? []) as any[];

  const createMut = trpc.lembretes.agendamentos.create.useMutation({
    onSuccess: () => {
      toast.success("Agendamento criado");
      agendamentosQuery.refetch();
      setModalOpen(false);
    },
    onError: (e) => toast.error(e.message),
  });

  const updateMut = trpc.lembretes.agendamentos.update.useMutation({
    onSuccess: () => {
      toast.success("Agendamento atualizado");
      agendamentosQuery.refetch();
      setModalOpen(false);
    },
    onError: (e) => toast.error(e.message),
  });

  const deleteMut = trpc.lembretes.agendamentos.delete.useMutation({
    onSuccess: () => {
      toast.success("Agendamento removido");
      agendamentosQuery.refetch();
    },
    onError: (e) => toast.error(e.message),
  });

  function openCreate() {
    setEditingId(null);
    setForm({ ...emptyForm, emitenteId: emitenteIdFiltro ?? null });
    setModalOpen(true);
  }

  function openEdit(ag: any) {
    setEditingId(ag.id);
    setForm({
      emitenteId: ag.emitenteId,
      whatsapp: ag.whatsapp,
      tipo: ag.tipo,
      diasSemana: diasSemanaToArray(ag.diasSemana),
      horario: ag.horario?.slice(0, 5) ?? "",
      ativo: ag.ativo === 1 || ag.ativo === true,
    });
    setModalOpen(true);
  }

  function handleSave() {
    if (!form.whatsapp) { toast.error("Informe o número de WhatsApp"); return; }
    if (form.diasSemana.length === 0) { toast.error("Selecione ao menos um dia da semana"); return; }
    if (!form.horario) { toast.error("Informe o horário"); return; }

    const payload = {
      emitenteId: form.emitenteId,
      whatsapp: form.whatsapp,
      tipo: form.tipo,
      diasSemana: arrayToDiasSemana(form.diasSemana),
      horario: form.horario,
      ativo: form.ativo,
    };

    if (editingId) {
      updateMut.mutate({ id: editingId, ...payload });
    } else {
      createMut.mutate(payload);
    }
  }

  function toggleDia(dia: number) {
    setForm((f) => ({
      ...f,
      diasSemana: f.diasSemana.includes(dia)
        ? f.diasSemana.filter((d) => d !== dia)
        : [...f.diasSemana, dia],
    }));
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Lembretes de Gestão e Operação</h1>
          <p className="text-sm text-muted-foreground">Envio automático de lembretes via WhatsApp</p>
        </div>
        <Button onClick={openCreate}>Novo Agendamento</Button>
      </div>

      {/* Filtro de emitente */}
      <div className="flex items-center gap-3">
        <Label className="whitespace-nowrap">Emitente:</Label>
        <Select
          value={emitenteIdFiltro ? String(emitenteIdFiltro) : "todos"}
          onValueChange={(v) => setEmitenteIdFiltro(v === "todos" ? undefined : Number(v))}
        >
          <SelectTrigger className="w-72">
            <SelectValue placeholder="Todos os emitentes" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos os emitentes</SelectItem>
            {emitentes.map((e: any) => (
              <SelectItem key={e.id} value={String(e.id)}>
                {e.razaoSocial}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="agendamentos">
        <TabsList>
          <TabsTrigger value="agendamentos">Agendamentos</TabsTrigger>
          <TabsTrigger value="disparos">Log de Disparos</TabsTrigger>
        </TabsList>

        {/* Aba Agendamentos */}
        <TabsContent value="agendamentos">
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Emitente</TableHead>
                  <TableHead>WhatsApp</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Dias</TableHead>
                  <TableHead>Horário</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Último Disparo</TableHead>
                  <TableHead className="text-right">Ações</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {agendamentos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                      Nenhum agendamento cadastrado
                    </TableCell>
                  </TableRow>
                ) : (
                  agendamentos.map((ag: any) => (
                    <TableRow key={ag.id}>
                      <TableCell>{ag.emitenteNome ?? ag.emitenteId}</TableCell>
                      <TableCell className="font-mono">{ag.whatsapp}</TableCell>
                      <TableCell>{formatTipo(ag.tipo)}</TableCell>
                      <TableCell className="text-sm">{formatDiasSemana(ag.diasSemana)}</TableCell>
                      <TableCell>{ag.horario?.slice(0, 5)}</TableCell>
                      <TableCell>
                        <Badge variant={ag.ativo ? "default" : "secondary"}>
                          {ag.ativo ? "Ativo" : "Inativo"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {ag.ultimoDisparoKey ?? "—"}
                      </TableCell>
                      <TableCell className="text-right space-x-2">
                        <Button size="sm" variant="outline" onClick={() => openEdit(ag)}>
                          Editar
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => {
                            if (confirm("Remover este agendamento?")) {
                              deleteMut.mutate({ id: ag.id });
                            }
                          }}
                        >
                          Remover
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        {/* Aba Log de Disparos */}
        <TabsContent value="disparos">
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data/Hora</TableHead>
                  <TableHead>Emitente</TableHead>
                  <TableHead>WhatsApp</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Erro</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {disparos.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      Nenhum disparo registrado
                    </TableCell>
                  </TableRow>
                ) : (
                  disparos.map((d: any) => (
                    <TableRow key={d.id}>
                      <TableCell className="text-sm">
                        {d.disparadoEm
                          ? new Date(d.disparadoEm).toLocaleString("pt-BR")
                          : "—"}
                      </TableCell>
                      <TableCell>{d.emitenteId}</TableCell>
                      <TableCell className="font-mono">{d.whatsapp}</TableCell>
                      <TableCell>{formatTipo(d.tipo)}</TableCell>
                      <TableCell>
                        <Badge variant={d.status === "enviado" ? "default" : "destructive"}>
                          {d.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-destructive max-w-xs truncate">
                        {d.erro ?? "—"}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </TabsContent>
      </Tabs>

      {/* Modal de criação/edição */}
      <Dialog open={modalOpen} onOpenChange={(v) => { if (!v) setModalOpen(false); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingId ? "Editar Agendamento" : "Novo Agendamento"}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Emitente */}
            <div className="space-y-1">
              <Label>Emitente *</Label>
              <Select
                value={form.emitenteId ? String(form.emitenteId) : "todos"}
                onValueChange={(v) => setForm((f) => ({ ...f, emitenteId: v === "todos" ? null : Number(v) }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o emitente" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os emitentes</SelectItem>
                  {emitentes.map((e: any) => (
                    <SelectItem key={e.id} value={String(e.id)}>
                      {e.razaoSocial}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* WhatsApp */}
            <div className="space-y-1">
              <Label>Número WhatsApp *</Label>
              <Input
                placeholder="Ex: 11999999999"
                value={form.whatsapp}
                onChange={(e) => setForm((f) => ({ ...f, whatsapp: e.target.value }))}
              />
              <p className="text-xs text-muted-foreground">Somente números. DDD + número. Prefixo 55 adicionado automaticamente.</p>
            </div>

            {/* Tipo */}
            <div className="space-y-1">
              <Label>Tipo de Lembrete *</Label>
              <Select
                value={form.tipo}
                onValueChange={(v) => setForm((f) => ({ ...f, tipo: v as any }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIPOS_LEMBRETE.map((t) => (
                    <SelectItem key={t.value} value={t.value}>
                      {t.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Dias da semana */}
            <div className="space-y-2">
              <Label>Dias da Semana *</Label>
              <div className="flex flex-wrap gap-3">
                {DIAS_SEMANA.map((d) => (
                  <div key={d.value} className="flex items-center gap-1.5">
                    <Checkbox
                      id={`dia-${d.value}`}
                      checked={form.diasSemana.includes(d.value)}
                      onCheckedChange={() => toggleDia(d.value)}
                    />
                    <Label htmlFor={`dia-${d.value}`} className="cursor-pointer font-normal">
                      {d.label}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Horário */}
            <div className="space-y-1">
              <Label>Horário (Brasília) *</Label>
              <Input
                type="time"
                value={form.horario}
                onChange={(e) => setForm((f) => ({ ...f, horario: e.target.value }))}
              />
            </div>

            {/* Status */}
            <div className="flex items-center gap-2">
              <Checkbox
                id="ativo"
                checked={form.ativo}
                onCheckedChange={(v) => setForm((f) => ({ ...f, ativo: !!v }))}
              />
              <Label htmlFor="ativo" className="cursor-pointer font-normal">Ativo</Label>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setModalOpen(false)}>Cancelar</Button>
              <Button
                onClick={handleSave}
                disabled={createMut.isPending || updateMut.isPending}
              >
                {editingId ? "Salvar" : "Criar"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
